import { useState } from 'react'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'

import type { Entity } from '@models/Entity'

import { StyledError } from '../../../../theme/components/StyledError'
import { useCreateContactpersoonForEntity } from '../../hooks/useCreateContactpersoonForEntity'
import type { AccountOption } from '../../types/AccountOption'

import { CreateAccountFields } from './CreateAccountFields'
import { CreateExternFields } from './CreateExternFields'
import type { ExternFields } from './CreateExternFields'

interface Props {
  entity: Entity
  onClose: () => void
}

export function Create({ entity, onClose }: Props) {
  const { mutate: createContactpersoon, error } =
    useCreateContactpersoonForEntity()

  const [mode, setMode] = useState<'account' | 'extern'>('account')
  const [account, setAccount] = useState<AccountOption | null>(null)
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [extern, setExtern] = useState<ExternFields>({
    naam: '',
    rol: '',
    contactOrganisatie: '',
    telefoonnummer: '',
    emailadres: '',
  })

  const validate = () => {
    const { naam, rol, emailadres, telefoonnummer } = extern
    const naamError = !naam
    const rolError = !rol
    const emailadresError = !emailadres
    const telefoonnummerError = !telefoonnummer

    setErrors({
      naamError,
      rolError,
      emailadresError,
      telefoonnummerError,
    })

    return !(naamError || rolError || emailadresError || telefoonnummerError)
  }

  const onSubmitClick = () => {
    if (mode === 'account') {
      setErrors({ accountError: !account })

      if (account) {
        createContactpersoon(
          {
            entity,
            contactpersoon: { type: 'account', userid: account.userid },
          },
          { onSuccess: () => onClose() },
        )
      }
    } else if (validate()) {
      createContactpersoon(
        {
          entity,
          contactpersoon: extern,
        },
        { onSuccess: () => onClose() },
      )
    }
  }

  return (
    <>
      <DialogTitle>
        Aanmaken contactpersoon voor {entity.type} {entity.id}
      </DialogTitle>
      <DialogContent>
        <StyledError error={error} />
        <Box>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>Let op!</AlertTitle>U bent ervoor verantwoordelijk om te
            melden dat contactgegevens worden verwerkt.
          </Alert>
        </Box>
        <ToggleButtonGroup
          value={mode}
          exclusive
          color="primary"
          size="small"
          sx={{ mb: 2 }}
          onChange={(_, value) => {
            if (value !== null) {
              setErrors({})
              setMode(value)
            }
          }}
        >
          <ToggleButton value="account">Kies uit CAPO accounts</ToggleButton>
          <ToggleButton value="extern">Vrij invullen</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'account' ? (
          <CreateAccountFields
            entity={entity}
            account={account}
            onAccountChange={setAccount}
            error={errors['accountError']}
          />
        ) : (
          <CreateExternFields
            values={extern}
            onChange={setExtern}
            errors={errors}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Annuleren
        </Button>
        <Button color="primary" onClick={onSubmitClick}>
          Opslaan
        </Button>
      </DialogActions>
    </>
  )
}
